import { makeAutoObservable } from "mobx";
import dataRequest from "../http/DataRequest";
import { aggregateOlapRowsByDate, safeNum } from "../utils/iikoOlapGroup";

const METRICS = [
  { key: "revenue", label: "Выручка", field: "DishDiscountSumInt", mode: "sum", unit: "₽" },
  { key: "guests", label: "Гостей", field: "GuestNum", mode: "sum", unit: "" },
  { key: "orders", label: "Чеков", field: "UniqOrderId", mode: "sum", unit: "" },
  { key: "avgCheck", label: "Средний чек", field: "DishDiscountSumInt.average", mode: "avg", unit: "₽" },
  { key: "foodCost", label: "Себестоимость", field: "ProductCostBase.Percent", mode: "avg", unit: "%", scale: 100 },
  { key: "returns", label: "Возвраты", field: "DishReturnSum", mode: "sum", unit: "₽" },
];

function aggregateMetric(rows, field, mode, scale = 1) {
  const aggregated = aggregateOlapRowsByDate(rows);
  if (!aggregated.length) return 0;
  const vals = aggregated.map((r) => safeNum(r[field]) * scale);
  if (mode === "avg") {
    return vals.reduce((a, b) => a + b, 0) / vals.length;
  }
  return vals.reduce((a, b) => a + b, 0);
}

function buildComparison(rowsA, rowsB) {
  return METRICS.map((m) => {
    const valA = aggregateMetric(rowsA, m.field, m.mode, m.scale ?? 1);
    const valB = aggregateMetric(rowsB, m.field, m.mode, m.scale ?? 1);
    const delta = valB - valA;
    const deltaPct = valA !== 0 ? (delta / Math.abs(valA)) * 100 : null;
    return { ...m, valA, valB, delta, deltaPct };
  });
}

class CompareStore {
  periodA = { from: null, to: null };
  periodB = { from: null, to: null };
  rowsA = [];
  rowsB = [];
  comparison = [];
  isLoading = false;
  errorMessage = null;

  constructor() {
    makeAutoObservable(this);
  }

  setPeriodA(from, to) {
    this.periodA = { from, to };
  }

  setPeriodB(from, to) {
    this.periodB = { from, to };
  }

  async fetchCompare(serverId, fromA, toA, fromB, toB, restaurantGroup = null) {
    if (!serverId || !fromA || !toA || !fromB || !toB) return;
    this.isLoading = true;
    this.errorMessage = null;
    try {
      const groupBy = Boolean(restaurantGroup);
      // Sequential, not Promise.all — iiko's API guidance asks for one request
      // at a time against a given login, same reasoning as every backend OLAP
      // call (see helpers/dateRange.js); two periods means two separate iiko
      // sessions opened back to back here, never concurrently.
      const respA = await dataRequest.getIikoOlapData(serverId, fromA, toA, groupBy, restaurantGroup);
      const respB = await dataRequest.getIikoOlapData(serverId, fromB, toB, groupBy, restaurantGroup);
      this.rowsA = Array.isArray(respA?.data) ? respA.data : [];
      this.rowsB = Array.isArray(respB?.data) ? respB.data : [];
      this.comparison = buildComparison(this.rowsA, this.rowsB);
      this.periodA = { from: fromA, to: toA };
      this.periodB = { from: fromB, to: toB };
    } catch (e) {
      this.errorMessage = e?.response?.data?.message || e?.message || "Ошибка загрузки";
      this.comparison = [];
    } finally {
      this.isLoading = false;
    }
  }
}

const compareStore = new CompareStore();
export default compareStore;
